import { useState, useCallback, useEffect, useRef } from 'react';
import { createRevision, getRevisions, getRevisionPdfData } from '../services/revisions';
import { CompileDiagnostic, parseCompileDiagnostics, parseCompileErrorWithContext } from '../services/compileErrors';
import { ProjectFile } from '../types';
import { isStorageBackedFile } from '../services/projects';

/**
 * Characters that are special in LaTeX and unsafe in file names sent to the
 * compiler.  Underscores (`_`) are the most common offender — LaTeX treats
 * them as subscript operators, so `\input{my_file}` will error out.
 */
const UNSAFE_FILENAME_RE = /[_#%&{}~^]/g;

/**
 * Map of common non-ASCII characters to ASCII equivalents.  Applied to code
 * files (anything that isn't .tex or .bib) before sending them to pdflatex,
 * which can't render these inside \lstinputlisting / \VerbatimInput blocks.
 */
const UNICODE_TO_ASCII: [RegExp, string][] = [
  [/[\u2018\u2019\u201A]/g, "'"],   // smart single quotes
  [/[\u201C\u201D\u201E]/g, '"'],   // smart double quotes
  [/\u2014/g, '--'],                 // em-dash
  [/\u2013/g, '-'],                  // en-dash
  [/\u2026/g, '...'],               // ellipsis
  [/\u00A0/g, ' '],                 // non-breaking space
  [/\u2190/g, '<-'],                // left arrow
  [/\u2192/g, '->'],                // right arrow
  [/\u2264/g, '<='],                // less-than-or-equal
  [/\u2265/g, '>='],                // greater-than-or-equal
  [/\u2260/g, '!='],                // not-equal
  [/\u2248/g, '~='],                // approximately equal
  [/\u00D7/g, 'x'],                // multiplication sign
  [/\u00B7/g, '*'],                // middle dot
  [/[\uFEFF\u200B-\u200F]/g, ''],  // zero-width / BOM
];

/** Replace non-ASCII characters in code files with ASCII equivalents. */
function sanitizeCodeContent(content: string): string {
  let result = content;
  for (const [pattern, replacement] of UNICODE_TO_ASCII) {
    result = result.replace(pattern, replacement);
  }
  return result;
}

/** Replace unsafe LaTeX characters in a filename with a hyphen. */
function sanitizeFileName(name: string): string {
  const ext = name.lastIndexOf('.');
  if (ext <= 0) return name.replace(UNSAFE_FILENAME_RE, '-');
  return name.slice(0, ext).replace(UNSAFE_FILENAME_RE, '-') + name.slice(ext);
}

/**
 * Build a map from original file name → sanitized name, then rewrite all
 * `\input`, `\include`, `\lstinputlisting`, `\bibliography`, `\includegraphics`,
 * and similar commands in `content` so they use the sanitized paths.
 */
function rewriteFileReferences(
  content: string,
  nameMap: Map<string, string>
): string {
  if (nameMap.size === 0) return content;

  // Pattern matches LaTeX commands that take a file-path argument:
  //   \input{path}  \include{path}  \lstinputlisting[...]{path}
  //   \bibliography{p1,p2}  \includegraphics[...]{path}
  //   \VerbatimInput{path}  \verbatiminput{path}
  const cmdPattern =
    /\\(?:input|include|lstinputlisting|bibliography|includegraphics|VerbatimInput|verbatiminput)(?:\[[^\]]*\])?\{([^}]+)\}/g;

  return content.replace(cmdPattern, (match, innerPath: string) => {
    // bibliography can be comma-separated
    const parts = innerPath.split(',').map((p: string) => p.trim());
    let changed = false;

    const rewritten = parts.map((part) => {
      // Try exact match, then with common extensions
      for (const candidate of [part, `${part}.tex`, `${part}.bib`, `${part}.png`, `${part}.jpg`, `${part}.jpeg`]) {
        const sanitized = nameMap.get(candidate);
        if (sanitized != null) {
          changed = true;
          // Strip extension if the original reference omitted it
          return part.includes('.') ? sanitized : sanitized.replace(/\.[^.]+$/, '');
        }
      }
      return part;
    });

    if (!changed) return match;
    return match.replace(innerPath, rewritten.join(','));
  });
}

export interface CompilationResult {
  pdf: Uint8Array | null;
  log: string;
  errors: string[];
  diagnostics: CompileDiagnostic[];
}

interface CompileOptions {
  writeArtifacts?: boolean;
}

interface UseLatexCompilerReturn {
  compile: (
    texContent: string,
    allFiles: ProjectFile[],
    options?: CompileOptions
  ) => Promise<CompilationResult>;
  validateCompile: (
    texContent: string,
    allFiles: ProjectFile[]
  ) => Promise<CompilationResult>;
  compiling: boolean;
  result: CompilationResult | null;
  error: string | null;
  engineReady: boolean;
  revisionCount: number;
  revisionLoading: boolean;
  replaceResult: (nextResult: CompilationResult | null) => void;
}

function buildCompilationError(
  err: unknown,
  texContent: string,
  allFiles: ProjectFile[]
): CompilationResult {
  const texFile = allFiles.find((f) => f.type === 'tex' && f.content === texContent);
  const mainFileName = texFile?.name || 'main.tex';
  const rawError = err instanceof Error ? err.message : 'Compilation failed';
  const diagnostics = parseCompileDiagnostics(rawError, mainFileName);
  const parsed = parseCompileErrorWithContext(rawError, allFiles);
  const errorMessage = parsed.suggestion
    ? `${parsed.title}\n${parsed.message}\n${parsed.suggestion}`
    : `${parsed.title}\n${parsed.message}`;

  return {
    pdf: null,
    log: errorMessage,
    errors: [errorMessage],
    diagnostics,
  };
}

async function runCompileRequest(
  texContent: string,
  allFiles: ProjectFile[]
): Promise<CompilationResult> {
  const texFile = allFiles.find((f) => f.type === 'tex' && f.content === texContent);
  const mainFileName = texFile?.name || 'main.tex';

  // Build original→sanitized name map for files that need renaming
  const nameMap = new Map<string, string>();
  for (const file of allFiles) {
    if (file.type === 'folder') continue;
    const safe = sanitizeFileName(file.name);
    if (safe !== file.name) {
      nameMap.set(file.name, safe);
    }
  }

  const safeMainFileName = nameMap.get(mainFileName) ?? mainFileName;

  // Rewrite \input / \include / etc. references in the main file content
  const safeTexContent = rewriteFileReferences(texContent, nameMap);

  const resources: Array<{ main?: boolean; path?: string; content?: string; file?: string }> = [
    { main: true, path: safeMainFileName, content: safeTexContent },
  ];

  for (const file of allFiles) {
    if (file.type === 'folder') continue;
    if (isStorageBackedFile(file)) continue;
    if (file.content === texContent) continue;

    const safeName = nameMap.get(file.name) ?? file.name;

    if (file.type === 'image') {
      const imgResponse = await fetch(file.content);
      const blob = await imgResponse.blob();
      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result as string;
          resolve(dataUrl.split(',')[1]);
        };
        reader.readAsDataURL(blob);
      });
      resources.push({ path: safeName, file: base64 });
    } else {
      let safeContent: string;
      if (file.type === 'tex') {
        safeContent = rewriteFileReferences(file.content, nameMap);
      } else if (file.type === 'bib') {
        safeContent = file.content;
      } else {
        // Code files imported via \lstinputlisting — strip non-ASCII
        safeContent = sanitizeCodeContent(file.content);
      }
      resources.push({ path: safeName, content: safeContent });
    }
  }

  const hasBibFiles = allFiles.some((file) => (
    file.type === 'bib' || file.name.toLowerCase().endsWith('.bib')
  ));

  const requestBody: Record<string, any> = {
    compiler: 'pdflatex',
    resources,
    options: {
      compiler: { halt_on_error: true },
      response: { log_files_on_failure: true },
    },
  };

  if (hasBibFiles) {
    requestBody.options.compiler.bibliography = true;
  }

  const response = await fetch('https://latex.ytotech.com/builds/sync', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || 'Compilation failed');
  }

  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    const errorData = await response.json();
    throw new Error(JSON.stringify(errorData));
  }

  const pdfBuffer = await response.arrayBuffer();
  const pdfData = new Uint8Array(pdfBuffer);

  if (pdfData.byteLength === 0) {
    throw new Error('Compilation failed (empty PDF response)');
  }

  const header = new TextDecoder().decode(pdfData.slice(0, 5));
  if (header !== '%PDF-') {
    const text = new TextDecoder().decode(pdfData.slice(0, 1000));
    throw new Error(text || 'Compilation failed (invalid PDF response)');
  }

  return {
    pdf: pdfData,
    log: 'Compilation successful',
    errors: [],
    diagnostics: [],
  };
}

export function useLatexCompiler(projectId?: string): UseLatexCompilerReturn {
  const [compiling, setCompiling] = useState(false);
  const [result, setResult] = useState<CompilationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [revisionCount, setRevisionCount] = useState(0);
  const [revisionLoading, setRevisionLoading] = useState(!!projectId);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!projectId) {
      setRevisionLoading(false);
      return;
    }

    setRevisionLoading(true);
    getRevisions(projectId).then((revisions) => {
      setRevisionCount(revisions.length);
      if (revisions.length > 0) {
        const latestRevision = revisions[0];
        const loadLatestPdf = async (attempt: number) => {
          try {
            const pdfData = await getRevisionPdfData(projectId, latestRevision.id);
            setResult({
              pdf: pdfData,
              log: 'Loaded from revision history',
              errors: [],
              diagnostics: [],
            });
            setRevisionLoading(false);
          } catch {
            if (attempt < 1) {
              setTimeout(() => loadLatestPdf(attempt + 1), 1200);
            } else {
              setRevisionLoading(false);
            }
          }
        };
        loadLatestPdf(0);
      } else {
        setRevisionLoading(false);
      }
    }).catch(() => {
      setRevisionLoading(false);
    });
  }, [projectId]);

  const validateCompile = useCallback(async (
    texContent: string,
    allFiles: ProjectFile[]
  ): Promise<CompilationResult> => {
    try {
      return await runCompileRequest(texContent, allFiles);
    } catch (err) {
      return buildCompilationError(err, texContent, allFiles);
    }
  }, []);

  const compile = useCallback(async (
    texContent: string,
    allFiles: ProjectFile[],
    options?: CompileOptions
  ): Promise<CompilationResult> => {
    setCompiling(true);
    setError(null);

    try {
      const compilationResult = await runCompileRequest(texContent, allFiles);
      setResult(compilationResult);
      setCompiling(false);

      if (projectId && options?.writeArtifacts !== false && compilationResult.pdf) {
        const pdfDataForStorage = compilationResult.pdf.slice();
        const filesSnapshot = allFiles.map((file) => ({ ...file }));

        window.setTimeout(() => {
          void (async () => {
            try {
              await createRevision(projectId, filesSnapshot, pdfDataForStorage);
              if (isMountedRef.current) {
                setRevisionCount((prev) => prev + 1);
              }
            } catch (revisionError) {
              console.warn('Failed to save revision:', revisionError);
            }
          })();
        }, 0);
      }

      return compilationResult;
    } catch (err) {
      const compilationResult = buildCompilationError(err, texContent, allFiles);
      setError(compilationResult.log);
      setResult(compilationResult);
      setCompiling(false);
      return compilationResult;
    }
  }, [projectId]);

  const replaceResult = useCallback((nextResult: CompilationResult | null) => {
    setResult(nextResult);
  }, []);

  return {
    compile,
    validateCompile,
    compiling,
    result,
    error,
    engineReady: true,
    revisionCount,
    revisionLoading,
    replaceResult,
  };
}
